import React, { useState, useRef, useEffect } from 'react';
import Portal from './Portal';
import Backdrop from './Backdrop';
import GlassButton from './Button';
import { CheckIcon, CloseIcon } from './Icons';
// --- INÍCIO DA ALTERAÇÃO ---
import CustomDatePicker from './CustomDatePicker';
// --- FIM DA ALTERAÇÃO ---


const EditorPopup = ({ value, onSave, onCancel, editorType, options, title }) => {
    const [currentValue, setCurrentValue] = useState(value);
    const editorRef = useRef(null);

    useEffect(() => {
        // Foca no editor apenas se não for o de data, que não tem um "foco" tradicional
        if (editorType !== 'date') {
            editorRef.current?.focus();
        }
    }, [editorType]);
    
    const handleSave = () => {
        if (currentValue !== value) {
            onSave(currentValue);
        }
        onCancel();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && editorType !== 'textarea' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        }
        if (e.key === 'Escape') {
            onCancel();
        }
    };
    
    const renderEditor = () => {
        const commonClasses = "w-full p-2 bg-black/20 rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-indigo-400/80 transition-all duration-200";
        switch (editorType) {
            case 'select':
                return (
                    <div className="relative">
                        <select
                            ref={editorRef}
                            value={currentValue}
                            onChange={(e) => setCurrentValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className={`${commonClasses} appearance-none pr-8 text-white`}
                        >
                            {options.map(option => (
                                <option key={option.value} value={option.value} className="bg-gray-800">{option.label}</option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-300">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                        </div>
                    </div>
                );
            // --- INÍCIO DA ALTERAÇÃO ---
            case 'date':
                const handleDateChangeAndSave = (date) => {
                    setCurrentValue(date);
                    // Salva e fecha automaticamente ao selecionar uma data
                    onSave(date); 
                    onCancel();
                };
                return <CustomDatePicker value={currentValue} onChange={handleDateChangeAndSave} />;
            // --- FIM DA ALTERAÇÃO ---
            case 'textarea':
                return <textarea ref={editorRef} value={currentValue} onChange={(e) => setCurrentValue(e.target.value)} onKeyDown={handleKeyDown} className={`${commonClasses} h-40 resize-none text-white`} />;
            default:
                return <input ref={editorRef} type="text" value={currentValue} onChange={(e) => setCurrentValue(e.target.value)} onKeyDown={handleKeyDown} className={`${commonClasses} text-white`} />;
        }
    };

    return (
        <div className="bg-gray-800/80 backdrop-filter backdrop-blur-lg border border-white/20 rounded-2xl shadow-lg w-full max-w-md p-6 animate-scale-in">
            <h3 className="text-lg font-bold text-white mb-4">{title || "Editar Informação"}</h3>
            <div className="space-y-4">
                {renderEditor()}
            </div>
            {/* Para o editor de data, os botões são ocultados pois a ação é instantânea */}
            {editorType !== 'date' && (
                <div className="flex justify-end space-x-2 mt-6">
                    <GlassButton onClick={onCancel} className="!bg-white/10 hover:!bg-white/20 !px-4 !py-2 !rounded-lg !font-semibold flex items-center gap-2">
                        <CloseIcon size={16} /> Cancelar
                    </GlassButton>
                    <GlassButton onClick={handleSave} className="!bg-indigo-500 hover:!bg-indigo-600 !px-4 !py-2 !rounded-lg !font-semibold flex items-center gap-2">
                        <CheckIcon size={16} /> Salvar
                    </GlassButton>
                </div>
            )}
        </div>
    );
};


const InlineEditor = ({ value, onSave, editorType = 'text', children, options = [], title }) => {
    const [isEditing, setIsEditing] = useState(false);
    
    return (
        <div className="w-full">
            <div onClick={() => setIsEditing(true)} className="cursor-pointer w-full p-1 -m-1 rounded-md hover:bg-white/10 transition-colors">
                {children}
            </div>
            {isEditing && (
                <Portal>
                    <Backdrop onClick={() => setIsEditing(false)} />
                    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                        <EditorPopup 
                            value={value}
                            onSave={onSave}
                            onCancel={() => setIsEditing(false)}
                            editorType={editorType}
                            options={options}
                            title={title}
                        />
                    </div>
                </Portal>
            )}
        </div>
    );
};

export default InlineEditor;